import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';

const SITE_NAME = 'MacTech';
const DEFAULT_DESCRIPTION = 'MacTech — iOS, Android ve Web uygulaması olarak hizmet veren işletme yönetim ekosistemi. Ti-Cari ailesi ile sektörünüzü dijitalleştirin. 14 gün ücretsiz deneyin.';
const DEFAULT_KEYWORDS = 'MacTech, Ti-Cari, Ti-Cari Galeri, Ti-Cari Emlak, mobil uygulama, iOS, Android, Play Store, App Store, işletme yönetim yazılımı, SaaS, Türkiye';
const BASE_URL = 'https://www.mactech.tr';
const DEFAULT_IMAGE = '/logo.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SEO = ({
    title,
    description,
    keywords,
    canonical,
    path = '',
    image,
    type = 'website',
    noindex = false,
    schema,
    breadcrumbs,
    faq,
    softwareApp,
    aggregateRating
}) => {
    // Fetch CMS-managed meta for this path (admin-editable)
    const [cmsMeta, setCmsMeta] = useState(null);
    useEffect(() => {
        if (!path) return;
        let cancelled = false;
        fetch(`${API_URL}/api/page-meta?path=${encodeURIComponent(path)}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!cancelled && d) setCmsMeta(d); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [path]);

    const metaTitle = title || cmsMeta?.title;
    const metaDescription = description || cmsMeta?.description || DEFAULT_DESCRIPTION;
    const metaKeywords = keywords || cmsMeta?.keywords || DEFAULT_KEYWORDS;
    const metaImage = image || cmsMeta?.og_image || DEFAULT_IMAGE;

    const fullTitle = metaTitle
        ? (metaTitle.includes(SITE_NAME) ? metaTitle : `${metaTitle} | ${SITE_NAME}`)
        : `${SITE_NAME} - Ti-Cari Ekosistemi | iOS, Android ve Web`;
    const fullUrl = `${BASE_URL}${path}`;
    const fullImage = metaImage.startsWith('http') ? metaImage : `${BASE_URL}${metaImage}`;
    const canonicalUrl = canonical || fullUrl;

    const breadcrumbSchema = breadcrumbs ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": item.name,
            "item": `${BASE_URL}${item.path}`
        }))
    } : null;

    const faqSchema = faq && faq.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faq.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    } : null;

    const softwareSchema = softwareApp ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": softwareApp.name,
        "description": softwareApp.description,
        "applicationCategory": softwareApp.category || "BusinessApplication",
        "operatingSystem": "Web, iOS, Android",
        "url": `${BASE_URL}${softwareApp.path || ''}`,
        "offers": softwareApp.price ? {
            "@type": "Offer",
            "price": softwareApp.price,
            "priceCurrency": "TRY",
            "availability": "https://schema.org/InStock"
        } : undefined,
        ...(aggregateRating && aggregateRating.reviewCount > 0 ? {
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": aggregateRating.ratingValue,
                "reviewCount": aggregateRating.reviewCount,
                "bestRating": aggregateRating.bestRating || 5,
                "worstRating": aggregateRating.worstRating || 1
            }
        } : {})
    } : null;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={metaKeywords} />
            {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content="tr_TR" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={fullImage} />

            {/* Structured Data */}
            {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
            {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
            {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
            {softwareSchema && <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>}
        </Helmet>
    );
};
